import React from 'react'

interface SettingsCogIconProps {
    className?: string
    size?: number
    variant?: 'default' | 'active' | 'hover'
}

export function SettingsCogIcon({
    className = '',
    size = 24,
    variant = 'default'
}: SettingsCogIconProps) {
    const colors = {
        default: {
            primary: '#94a3b8',
            secondary: '#64748b',
            accent: '#fb2ba9',
            glow: 'rgba(251, 43, 169, 0.2)'
        },
        active: {
            primary: '#fb2ba9',
            secondary: '#e91e8c',
            accent: '#fb2ba9',
            glow: 'rgba(251, 43, 169, 0.4)'
        },
        hover: {
            primary: '#cbd5e1',
            secondary: '#94a3b8',
            accent: '#fb2ba9',
            glow: 'rgba(251, 43, 169, 0.3)'
        }
    }

    const c = colors[variant]

    return (
        <>
            <style>{`
        @keyframes cog-spin-hold {
          0% { 
            transform: rotate(0deg);
            fill: ${c.secondary};
          }
          /* Quickly transition into the gradient view */
          15% {
            fill: url(#settings-gradient-light);
          }
          /* Near the end of spin, still showing gradient */
          85% {
             fill: url(#settings-gradient-light);
          }
          /* Final state: Finish rotation and snap to solid Cyan hold */
          100% { 
            transform: rotate(180deg);
            fill: #00FFC0; 
          }
        }

        .settings-hover .cog-main {
          /* 'forwards' ensures it holds the 100% state (Cyan) at the end */
          animation: cog-spin-hold 1.2s ease-in-out forwards;
          transform-origin: 12px 12px;
        }
      `}</style>

            <svg
                width={size}
                height={size}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={`${className} ${variant === 'hover' ? 'settings-hover' : ''}`}
            >
                <defs>
                    {/* Revised Lighter Gradient: Pink -> bright Violet -> Cyan dominated */}
                    <linearGradient id="settings-gradient-light" x1="4" y1="4" x2="20" y2="20" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#CC339F" />   {/* Pink */}
                        <stop offset="25%" stopColor="#A855F7" />  {/* Lighter, brighter Purple, passed quickly */}
                        <stop offset="100%" stopColor="#00FFC0" /> {/* Cyan takes over */}
                    </linearGradient>
                </defs>

                {/* Active Glow */}
                {variant === 'active' && (
                    <circle cx="12" cy="12" r="10" fill={c.glow} opacity="0.6" />
                )}

                {/* The Cog Path */}
                <path
                    className="cog-main"
                    d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1Z"
                    fill={c.secondary}
                    stroke={variant === 'active' ? c.accent : 'transparent'}
                    strokeWidth="1"
                />

                {/* Shared Content Dots */}
                <circle cx="7.5" cy="7.5" r="1" fill="currentColor" opacity="0.4" />
                <circle cx="7.5" cy="16.5" r="1" fill="currentColor" opacity="0.4" />
                <circle cx="16.5" cy="16.5" r="1" fill="currentColor" opacity="0.4" />

                {/* Accent dot for active state */}
                {variant === 'active' && (
                    <circle cx="19" cy="6" r="2" fill={c.accent} />
                )}
            </svg>
        </>
    )
}